import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import { ArrowPathIcon } from '../../components/Icons';
import { DataListTable, type Column } from '../../components/crm/DataListTable';

interface PlanPrice {
  id: string;
  plan_code: string;
  stripe_price_id: string;
  stripe_mode: string;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface PlanReference {
  plan_code: string;
  display_name: string;
  billing_interval: string;
  actual_price: number;
}

const Plans: React.FC = () => {
  const [plans, setPlans] = useState<PlanPrice[]>([]);
  const [reference, setReference] = useState<PlanReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editPlan, setEditPlan] = useState<PlanPrice | null>(null);
  const [form, setForm] = useState({ plan_code: '', stripe_price_id: '', stripe_mode: 'live' as 'test' | 'live' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const [plansData, refData] = await Promise.all([
        apiClient.get('/platform/admin/plans/'),
        apiClient.get('/platform/admin/plans/reference/'),
      ]);
      setPlans(plansData);
      setReference(refData);
    } catch {
      setPlans([]);
      setReference([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const openAddModal = () => {
    setForm({ plan_code: '', stripe_price_id: '', stripe_mode: 'live' });
    setError(null);
    setShowAddModal(true);
  };

  const openEditModal = (p: PlanPrice) => {
    setEditPlan(p);
    setForm({
      plan_code: p.plan_code,
      stripe_price_id: p.stripe_price_id,
      stripe_mode: (p.stripe_mode as 'test' | 'live') || 'live',
    });
    setError(null);
  };

  const handleSubmitAdd = async () => {
    if (!form.plan_code.trim() || !form.stripe_price_id.trim()) {
      setError('Plan code and Stripe Price ID are required');
      return;
    }
    if (!form.stripe_price_id.startsWith('price_')) {
      setError('Stripe Price ID must start with price_');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/platform/admin/plans/create/', {
        plan_code: form.plan_code.trim(),
        stripe_price_id: form.stripe_price_id.trim(),
        stripe_mode: form.stripe_mode,
      });
      setShowAddModal(false);
      await fetchPlans();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!editPlan) return;
    if (!form.stripe_price_id.trim()) {
      setError('Stripe Price ID is required');
      return;
    }
    if (!form.stripe_price_id.startsWith('price_')) {
      setError('Stripe Price ID must start with price_');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.patch(`/platform/admin/plans/${editPlan.id}/`, {
        stripe_price_id: form.stripe_price_id.trim(),
        stripe_mode: form.stripe_mode,
      });
      setEditPlan(null);
      await fetchPlans();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (p: PlanPrice) => {
    if (!window.confirm(`Deactivate plan "${p.plan_code}"? It will no longer be available for checkout.`)) return;
    try {
      await apiClient.delete(`/platform/admin/plans/${p.id}/delete/`);
      await fetchPlans();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to deactivate');
    }
  };

  const columns = useMemo<Column<PlanPrice>[]>(
    () => [
      {
        id: 'plan_code',
        header: 'Plan Code',
        mobilePriority: 'primary',
        mobileLabel: 'Plan Code',
        render: (p) => <span className="text-sm font-medium text-white">{p.plan_code}</span>,
      },
      {
        id: 'stripe_price_id',
        header: 'Stripe Price ID',
        mobileLabel: 'Stripe Price ID',
        render: (p) => <span className="text-sm text-fg-muted font-mono">{p.stripe_price_id}</span>,
      },
      {
        id: 'mode',
        header: 'Mode',
        mobileLabel: 'Mode',
        render: (p) => (
          <span
            className={`inline-flex px-2 py-0.5 rounded text-xs ${
              p.stripe_mode === 'live' ? 'bg-success-bg text-success-fg' : 'bg-neutral-badge-bg text-neutral-badge-fg'
            }`}
          >
            {p.stripe_mode}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (p) => (
          <span
            className={`inline-flex px-2 py-0.5 rounded text-xs ${
              p.is_active ? 'bg-success-bg text-success-fg' : 'bg-neutral-badge-bg text-neutral-badge-fg'
            }`}
          >
            {p.is_active ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        id: 'updated',
        header: 'Updated',
        mobileLabel: 'Updated',
        render: (p) => (
          <span className="text-sm text-fg-muted">
            {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        mobileLabel: 'Actions',
        render: (p) =>
          p.is_active ? (
            <div data-row-action className="flex gap-2">
              <button
                onClick={() => openEditModal(p)}
                className="text-xs px-2 py-1 rounded bg-primary hover:bg-primary-dark text-primary-fg"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeactivate(p)}
                className="text-xs px-2 py-1 rounded bg-red-900/50 hover:bg-red-900/70 text-red-300"
              >
                Deactivate
              </button>
            </div>
          ) : null,
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Plans & Pricing</h1>
          <p className="text-sm text-fg-muted mt-1">
            Stripe Price IDs for billing plans. Used for Checkout redirect when tenants purchase plans.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fetchPlans()}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-fg-secondary bg-surface-muted border border-border rounded-lg hover:bg-surface-muted flex items-center gap-2 disabled:opacity-50"
          >
            <ArrowPathIcon size={4} className={loading ? 'animate-spin' : undefined} />
            Refresh
          </button>
          <button
            onClick={openAddModal}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg"
          >
            Add Plan
          </button>
        </div>
      </div>

      <DataListTable<PlanPrice>
        columns={columns}
        rows={plans}
        getRowKey={(p) => p.id}
        loading={loading}
        getRowClassName={(p) => (!p.is_active ? 'opacity-60' : undefined)}
        emptyContent={
          <div className="px-4 py-8 text-center text-fg-muted">
            No plan prices configured. Add plans from the Stripe Dashboard, then map them here.
          </div>
        }
      />

      {!loading && reference.length > 0 && (
            <div className="bg-app border border-border rounded-lg p-4">
              <h2 className="text-sm font-medium text-fg-muted mb-2">Known plan codes (reference)</h2>
              <div className="flex flex-wrap gap-2">
                {reference.map((r) => (
                  <span
                    key={r.plan_code}
                    className="text-xs px-2 py-1 rounded bg-surface-muted text-fg-muted font-mono"
                    title={`${r.display_name} - $${r.actual_price}/${r.billing_interval}`}
                  >
                    {r.plan_code}
                  </span>
                ))}
              </div>
            </div>
          )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowAddModal(false)}>
          <div
            className="bg-app border border-border rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-fg mb-4">Add Plan Price</h3>
            {error && <div className="mb-3 text-sm text-red-400">{error}</div>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-fg-muted mb-1">Plan Code</label>
                <input
                  type="text"
                  value={form.plan_code}
                  onChange={(e) => setForm({ ...form, plan_code: e.target.value })}
                  placeholder="LP_STARTER_MONTHLY"
                  className="w-full px-3 py-2 bg-input border border-border rounded-lg text-fg placeholder-fg-muted"
                />
              </div>
              <div>
                <label className="block text-sm text-fg-muted mb-1">Stripe Price ID</label>
                <input
                  type="text"
                  value={form.stripe_price_id}
                  onChange={(e) => setForm({ ...form, stripe_price_id: e.target.value })}
                  placeholder="price_1Abc..."
                  className="w-full px-3 py-2 bg-input border border-border rounded-lg text-fg placeholder-fg-muted font-mono"
                />
              </div>
              <div>
                <label className="block text-sm text-fg-muted mb-1">Mode</label>
                <select
                  value={form.stripe_mode}
                  onChange={(e) => setForm({ ...form, stripe_mode: e.target.value as 'test' | 'live' })}
                  className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-white"
                >
                  <option value="test">Test</option>
                  <option value="live">Live</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSubmitAdd}
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary-dark disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Add Plan'}
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 rounded-lg bg-surface-muted text-fg hover:bg-surface-muted/80"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setEditPlan(null)}>
          <div
            className="bg-app border border-border rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white mb-4">Edit Plan Price</h3>
            <p className="text-sm text-fg-muted mb-3">Plan: {editPlan.plan_code}</p>
            {error && <div className="mb-3 text-sm text-red-400">{error}</div>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-fg-muted mb-1">Stripe Price ID</label>
                <input
                  type="text"
                  value={form.stripe_price_id}
                  onChange={(e) => setForm({ ...form, stripe_price_id: e.target.value })}
                  placeholder="price_1Abc..."
                  className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-white font-mono"
                />
              </div>
              <div>
                <label className="block text-sm text-fg-muted mb-1">Mode</label>
                <select
                  value={form.stripe_mode}
                  onChange={(e) => setForm({ ...form, stripe_mode: e.target.value as 'test' | 'live' })}
                  className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-white"
                >
                  <option value="test">Test</option>
                  <option value="live">Live</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSubmitEdit}
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary-dark disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setEditPlan(null)}
                className="px-4 py-2 rounded-lg bg-surface-muted text-fg hover:bg-surface-muted/80"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Plans;
